import { rem } from "@certn/cascadia";
import { ActionList, ButtonGroup, Divider, Text } from "@certn/cascadia-react";
import { FixedNavigationButton } from "@certn/cascadia-react/components/button/FixedNavigationButton";
import Skeleton from "@certn/cascadia-react/patterns/skeleton/Skeleton";
import { usePeopleAPI } from "@certn/legacy-api";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { css } from "@emotion/css";
import { Fragment, useState } from "react";
import { Trans } from "react-i18next";

import { HistoryCard } from "./HistoryCard/HistoryCard";
import { EmptyState } from "../../components/EmptyState/EmptyState";

type Application = components["schemas"]["ApplicationsList"];

export function HistoryCards() {
  const [activeButton, setActiveButton] = useState("All");
  const { data: applications, isLoading } = usePeopleAPI(
    "/internal/people/applicant/list/"
  );
  const allApplications = (applications ?? [])
    .filter(({ activity_log, archived }) =>
      ["SUBMITTED", "COMPLETE", "DISPUTE", "DECLINE_REQUEST"].find(
        (status) =>
          status === activity_log[activity_log.length - 1]?.status || archived
      )
    )
    .sort((a, b) => {
      return (
        new Date(b.activity_log[b.activity_log.length - 1].created).getTime() -
        new Date(a.activity_log[a.activity_log.length - 1].created).getTime()
      );
    });
  const processingApplications = allApplications.filter(
    ({ activity_log, archived }) =>
      activity_log[activity_log.length - 1].status !==
        ("COMPLETE" || "DECLINE_REQUEST") && !archived
  );
  const completeApplications = allApplications.filter(
    ({ activity_log, archived }) =>
      activity_log[activity_log.length - 1].status ===
        ("COMPLETE" || "DECLINE_REQUEST") && !archived
  );
  const renderApplications = (applications: Application[]) => (
    <>
      {isLoading ? (
        <div
          data-testid="history-cards-skeleton"
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "23px",
          })}
        >
          <Skeleton height="24px" width="254px" />
          <div
            className={css({
              gap: "10px",
            })}
          >
            <Skeleton height="10px" width="205px" />
            <Skeleton height="10px" width="157px" />
            <Skeleton height="10px" width="157px" />
          </div>
        </div>
      ) : (
        <ActionList>
          {applications?.map((application) => {
            return (
              <Fragment key={application.applicant_id}>
                <HistoryCard
                  superteamName={
                    application.application.team?.superteam?.name ??
                    application.application.team?.name ??
                    ""
                  }
                  activityLog={application.activity_log}
                  archived={Boolean(application.archived)}
                  applicationExpiryDate={application.expiry_date}
                />
                <Divider />
              </Fragment>
            );
          })}
        </ActionList>
      )}
    </>
  );

  const EmptyProcessingBackgroundChecksCard = () => (
    <EmptyState
      illustration="check-processing"
      description={<Trans>You have no processing background checks.</Trans>}
    />
  );

  const EmptyCompletedBackgroundChecksCard = () => (
    <EmptyState
      illustration="check-success"
      description={<Trans>You have no completed background checks.</Trans>}
    />
  );

  return (
    <section aria-label="history">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: rem("space-4"),
        }}
      >
        <Text as="h2" style="global/heading-large">
          <Trans>History</Trans>
        </Text>
        <ButtonGroup>
          <FixedNavigationButton
            size="small"
            testID="dashboard-history-all-button"
            selected={activeButton === "All"}
            onPress={() => setActiveButton("All")}
          >
            <Trans>All</Trans>
          </FixedNavigationButton>
          <FixedNavigationButton
            size="small"
            testID="dashboard-history-processing-button"
            selected={activeButton === "Processing"}
            onPress={() => setActiveButton("Processing")}
          >
            <Trans>Processing</Trans>
          </FixedNavigationButton>
          <FixedNavigationButton
            size="small"
            testID="dashboard-history-complete-button"
            selected={activeButton === "Complete"}
            onPress={() => setActiveButton("Complete")}
          >
            <Trans>Complete</Trans>
          </FixedNavigationButton>
        </ButtonGroup>
      </div>
      {activeButton === "Processing" &&
        (processingApplications && processingApplications.length > 0 ? (
          renderApplications(processingApplications)
        ) : (
          <EmptyProcessingBackgroundChecksCard />
        ))}
      {activeButton === "Complete" &&
        (completeApplications && completeApplications.length > 0 ? (
          renderApplications(completeApplications)
        ) : (
          <EmptyCompletedBackgroundChecksCard />
        ))}
      {activeButton === "All" &&
        (allApplications && allApplications.length > 0 ? (
          renderApplications(allApplications)
        ) : (
          <EmptyCompletedBackgroundChecksCard />
        ))}
    </section>
  );
}
